import type {
  EngagementAccumulatorState,
  EngagementComponents,
  EngagementLevel,
  EngagementScoreBreakdown,
  NormalizedEngagementConfig,
} from '../../contracts/engagement';

import { EngagementLevelResolver } from './EngagementLevelResolver';

/**
 * Constantes de saturação para converter métricas absolutas em componentes
 * normalizados 0..1. Determinísticas e documentadas (sem IA, sem dados
 * externos, sem aleatoriedade).
 */
export const ENGAGEMENT_SATURATION = {
  /** Tempo ativo para saturar o componente activeTime (ms). */
  activeTimeMs: 60_000,
  /** Nº de cliques para saturar o componente click. */
  clicks: 10,
  /** Nº de entradas em viewport para saturar o componente viewport. */
  viewportEntries: 5,
  /** Nº de heartbeats para saturar o componente heartbeat. */
  heartbeats: 10,
  /** Nº de rage clicks para saturar a penalidade rageClick. */
  rageClicks: 3,
} as const;

function clamp01(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function ratio(part: number, total: number): number {
  if (total <= 0) return 0;
  return clamp01(part / total);
}

/**
 * Converte o estado acumulado em componentes 0..1, aplica pesos e penalidades,
 * e produz o breakdown completo (explicável) com clamp final 0..100.
 *
 * Mesma entrada → mesma saída.
 */
export class EngagementScoreCalculator {
  private readonly config: NormalizedEngagementConfig;

  private readonly levelResolver: EngagementLevelResolver;

  constructor(
    config: NormalizedEngagementConfig,
    levelResolver?: EngagementLevelResolver
  ) {
    this.config = config;
    this.levelResolver =
      levelResolver ?? new EngagementLevelResolver(config.thresholds);
  }

  computeComponents(state: EngagementAccumulatorState): EngagementComponents {
    const observedMs = state.visibleDurationMs + state.hiddenDurationMs;
    return {
      activeTime: clamp01(
        state.activeDurationMs / ENGAGEMENT_SATURATION.activeTimeMs
      ),
      scroll: clamp01(state.maxScrollPercent / 100),
      click: clamp01(state.clickCount / ENGAGEMENT_SATURATION.clicks),
      viewport: clamp01(
        state.viewportEntryCount / ENGAGEMENT_SATURATION.viewportEntries
      ),
      visibility: ratio(state.visibleDurationMs, observedMs),
      heartbeat: clamp01(
        state.heartbeatCount / ENGAGEMENT_SATURATION.heartbeats
      ),
    };
  }

  calculate(state: EngagementAccumulatorState): EngagementScoreBreakdown {
    const { weights, penalties } = this.config;
    const components = this.computeComponents(state);

    const positive =
      components.activeTime * weights.activeTime +
      components.scroll * weights.scroll +
      components.click * weights.click +
      components.viewport * weights.viewport +
      components.visibility * weights.visibility +
      components.heartbeat * weights.heartbeat;

    const totalDurationMs = state.activeDurationMs + state.idleDurationMs;
    const idlePenalty =
      penalties.idle * ratio(state.idleDurationMs, totalDurationMs);
    const rageClickPenalty =
      penalties.rageClick *
      clamp01(state.rageClickCount / ENGAGEMENT_SATURATION.rageClicks);

    const rawScore = (positive - idlePenalty - rageClickPenalty) * 100;
    const finalScore = Math.max(0, Math.min(100, Math.round(rawScore)));
    const level: EngagementLevel = this.levelResolver.resolve(finalScore);

    return {
      components,
      weights,
      penalties: {
        idle: idlePenalty,
        rageClick: rageClickPenalty,
      },
      rawScore,
      finalScore,
      level,
    };
  }
}
